import { BuildingMenuType } from '@/enums';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import type { BuildingCategory, BuildingInStoreInfo, BuildingMenu, FloorInfo, KioskSelectedCategory, PoiContent } from '@/services/dto';
import type { FacilityFilterConfig } from '@/stores/types/facilityStore';
import type { PoiContentSlide } from '@/types/shop';
import { sortCategories } from '@/utils/configStore';
import poiUtils from '@/utils/poi';
import { isDutyFreePoi, DUTY_FREE_BUILDING_ID, getPoiBuildingName, isBranchStorePoi, isBranchBuildingCode } from '@/utils/shop';

const optionAllCategory: KioskSelectedCategory = {
    clientCode: 'all',
    name: 'common.text.all_category',
    id: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};

const getFloorCode = (floor: FloorInfo): string => (floor as FloorInfo & { floor_code?: string }).floor_code ?? floor.floorCode;

const getLocalizedContentValue = (value: Record<string, string> | undefined, lang: string): string => value?.[lang] || value?.['ko'] || '';

const getFacilityImageUrl = (contents: PoiContent['contents'], lang: string): string => {
    const legacyUrl = getLocalizedContentValue(contents.shopTmrkImgUrlApp, lang);
    if (legacyUrl) return legacyUrl;

    const logoImgPath = (contents as Record<string, Record<string, string> | undefined>).logoImgPath;
    return getLocalizedContentValue(logoImgPath, lang);
};

export const getFacilityMenu = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage?: string
): BuildingMenu | undefined => {
    const findMenu = (menus: BuildingMenu[]) =>
        menus.find((menu) => menu.menuCode === BuildingMenuType.FACILITIES || menu.menuType === BuildingMenuType.FACILITIES);

    const menu = findMenu(buildingMenus?.[language] ?? []);
    if (menu) return menu;

    if (mainLanguage && mainLanguage !== language) {
        return findMenu(buildingMenus?.[mainLanguage] ?? []);
    }

    return undefined;
};

export const getFacilityCategories = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string
): KioskSelectedCategory[] => {
    const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
    const selectedCategories = facilityMenu?.selectedCategories ?? [];

    if (!selectedCategories.length) return [optionAllCategory];

    return sortCategories([optionAllCategory, ...selectedCategories]);
};

export const getFacilityListCategories = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string
): KioskSelectedCategory[] => {
    const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
    const listCategory = facilityMenu?.listCategory ?? [];

    if (!listCategory.length) return [optionAllCategory];

    return sortCategories([optionAllCategory, ...listCategory]);
};

const findPoiFacilityCategory = (poiCategoryCodes: string[], facilityCategories: KioskSelectedCategory[]): BuildingCategory | undefined => {
    const matchedCategory = facilityCategories.find((category) => category.clientCode !== 'all' && poiCategoryCodes.includes(category.categoryCode));

    return matchedCategory as BuildingCategory | undefined;
};

export const resolveFacilityPoiContents = (
    facilityMenu: BuildingMenu | undefined,
    selectedBuildingId: string | null,
    searchMode: FACILITY_SEARCH_MODE
): PoiContent[] => {
    if (!facilityMenu) return [];

    const isFloorModeWithBuilding = searchMode === FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR && selectedBuildingId && selectedBuildingId !== 'all';

    if (isFloorModeWithBuilding && facilityMenu.poiContentBuildings?.length) {
        const match = facilityMenu.poiContentBuildings.find((b) => b.id === selectedBuildingId);
        return match?.poiContents ?? facilityMenu.poiContents ?? [];
    }

    return facilityMenu.poiContents ?? [];
};

export const convertPoiContentToSlide = (
    lang: string,
    buildings: BuildingInStoreInfo[],
    pois?: PoiContent[],
    facilityCategories?: KioskSelectedCategory[],
    storeCode?: string | null
): PoiContentSlide[] => {
    const buildingById = new Map(buildings.map((b) => [b.studioBuildingId, b]));
    const allPois: { poi: PoiContent; building: BuildingInStoreInfo }[] = pois
        ? pois.flatMap((poi) => {
              const building = buildingById.get(poi.studioBuildingId);
              return building ? [{ poi, building }] : [];
          })
        : [];

    return allPois.map(({ poi, building }) => {
        const contents = poi.contents;

        const name = poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'];
        const facilityUrl = getFacilityImageUrl(contents, lang);
        const floor = poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []);
        const isDutyFree = isDutyFreePoi(poi, lang);
        const defaultBuildingName = poiUtils.getBuildingLocalizedName(building, lang);
        const buildingName = getPoiBuildingName(poi, lang, defaultBuildingName, storeCode);
        const poiCategoryCodes = poiUtils.getPoiCategoryCodes(poi);
        const finalCategory = facilityCategories?.length ? findPoiFacilityCategory(poiCategoryCodes, facilityCategories) : undefined;

        return {
            id: poi.id,
            name: name || '',
            buildingName,
            floor,
            url: facilityUrl || '',
            data: poi,
            poiType: 'facility' as const,
            category: finalCategory ?? undefined,
            isDutyFreePoi: isDutyFree,
        };
    });
};

const matchFloor = (studioFloorId: string, selectedFloor: FacilityFilterConfig['selectedFloor']): boolean => {
    if (selectedFloor?.id === 'all') return true;

    if (Array.isArray(selectedFloor?.data)) {
        return (selectedFloor.data as string[]).includes(studioFloorId);
    }

    if (selectedFloor?.data) {
        return studioFloorId === getFloorCode(selectedFloor.data as FloorInfo);
    }

    return false;
};

export const filterByFloor = (facility: PoiContentSlide, config: FacilityFilterConfig): boolean => {
    const { selectedBuildingId, selectedFloor, buildings, language } = config;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(facility.data, language)) return false;
        return matchFloor(facility.data.studioFloorId, selectedFloor);
    }

    if (isBranchBuildingCode(selectedBuildingId)) {
        if (!isBranchStorePoi(facility.data, selectedBuildingId!, language)) return false;
        return matchFloor(facility.data.studioFloorId, selectedFloor);
    }

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(facility.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return facility.data.studioBuildingId === building.studioBuildingId;
        }

        return facility.data.studioFloorId === getFloorCode(selectedFloor?.data as FloorInfo) && facility.data.studioBuildingId === building.studioBuildingId;
    }

    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(facility.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (facility: PoiContentSlide, config: FacilityFilterConfig): boolean => {
    const { selectedCategory, selectedBuildingId, buildings, categoryCodesForFilter, language } = config;

    if (!selectedCategory) return false;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(facility.data, language)) return false;
    } else if (isBranchBuildingCode(selectedBuildingId)) {
        if (!isBranchStorePoi(facility.data, selectedBuildingId!, language)) return false;
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(facility.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (facility.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    if (!categoryCodesForFilter?.length) return false;

    const poiCategoryCodes = poiUtils.getPoiCategoryCodes(facility.data);
    return categoryCodesForFilter.some((code) => poiCategoryCodes.includes(code));
};

export const createFacilityFilter = (searchMode: FACILITY_SEARCH_MODE, config: FacilityFilterConfig) => {
    const filterMap = {
        [FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [FACILITY_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
    };

    const filterFunction = filterMap[searchMode];
    return (facility: PoiContentSlide) => {
        return filterFunction ? filterFunction(facility, config) : true;
    };
};

const facilityUtils = {
    convertPoiContentToSlide,
    filterByFloor,
    filterByCategory,
    createFacilityFilter,
    getFacilityMenu,
    getFacilityCategories,
    getFacilityListCategories,
    resolveFacilityPoiContents,
};

export default facilityUtils;
